import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  IFbCollapseState,
  IFbUiPreferences,
  FB_V2_DEFAULTS,
  FB_V2_STORAGE_KEY,
  FB_V2_COLLAPSE_KEY,
  FB_V2_SIDEBAR_MIN,
  FB_V2_SIDEBAR_MAX,
  FbBuilderMode,
} from '../models/form-builder-v2.models';

@Injectable()
export class FormBuilderV2StateService {
  private collapseState: IFbCollapseState = {};
  private preferences: IFbUiPreferences;

  builderMode$ = new BehaviorSubject<FbBuilderMode>('builder');
  sidebarWidth$ = new BehaviorSubject<number>(FB_V2_DEFAULTS.sidebarWidth);
  sidebarCollapsed$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.preferences = this.loadPreferences();
    this.collapseState = this.loadCollapseState();
    this.sidebarWidth$.next(this.preferences.sidebarWidth);
  }

  getPreferences(): IFbUiPreferences {
    return { ...this.preferences };
  }

  setPreference<K extends keyof IFbUiPreferences>(
    key: K,
    value: IFbUiPreferences[K]
  ): void {
    this.preferences[key] = value;
    this.savePreferences();
  }

  isCollapsed(fieldId: string): boolean {
    return this.collapseState[fieldId] ?? false;
  }

  toggleCollapse(fieldId: string): boolean {
    this.collapseState[fieldId] = !this.isCollapsed(fieldId);
    this.saveCollapseState();
    return this.collapseState[fieldId];
  }

  // persist=false during an active drag so we don't write localStorage on every
  // mousemove (that sync JSON.stringify + setItem is what made resizing janky);
  // the final width is persisted once on mouse-up.
  setSidebarWidth(width: number, persist = true): void {
    const clamped = Math.min(
      FB_V2_SIDEBAR_MAX,
      Math.max(FB_V2_SIDEBAR_MIN, width)
    );
    this.sidebarWidth$.next(clamped);
    if (persist) this.setPreference('sidebarWidth', clamped);
  }

  toggleSidebarCollapse(): void {
    this.sidebarCollapsed$.next(!this.sidebarCollapsed$.value);
  }

  setBuilderMode(mode: FbBuilderMode): void {
    this.builderMode$.next(mode);
  }

  private loadPreferences(): IFbUiPreferences {
    try {
      const stored = localStorage.getItem(FB_V2_STORAGE_KEY);
      return stored
        ? { ...FB_V2_DEFAULTS, ...JSON.parse(stored) }
        : { ...FB_V2_DEFAULTS };
    } catch {
      return { ...FB_V2_DEFAULTS };
    }
  }

  private savePreferences(): void {
    try {
      localStorage.setItem(FB_V2_STORAGE_KEY, JSON.stringify(this.preferences));
    } catch {
      // localStorage unavailable
    }
  }

  private loadCollapseState(): IFbCollapseState {
    try {
      const stored = localStorage.getItem(FB_V2_COLLAPSE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  }

  private saveCollapseState(): void {
    try {
      localStorage.setItem(
        FB_V2_COLLAPSE_KEY,
        JSON.stringify(this.collapseState)
      );
    } catch {
      // localStorage unavailable
    }
  }
}
